import { useCallback } from 'react';

import { checkoutStateModel, type TGlobalDataModel, type TGlobalDataModelAuditLog } from '@/_models';
import { dateTime, getCurrentSystemMode, useGetIp } from '@/_shared';

export function useSynchingCache(
  data: TGlobalDataModel,
  updateGlobalData: (globalData: TGlobalDataModel, isOverride?: boolean, overrideLock?: boolean) => void,
  overrideGlobalData: (globalData: TGlobalDataModel, isOverride?: boolean) => void
) {
  const { checkout, auditLog } = data;

  const ip = useGetIp();

  const getSystemData = useCallback(
    () => ({
      ipAddress: ip as string,
      userAgent: window.navigator.userAgent,
    }),
    [ip]
  );

  const getLogInformation = useCallback(() => {
    const isCustomerMode = getCurrentSystemMode();
    const by = isCustomerMode ? 'customer' : ('representative' as 'customer' | 'representative');
    const information = getSystemData();
    const at = dateTime.now().toISOString();

    return { by, information, at };
  }, [getSystemData]);

  const setCustomerTermsReviewed = useCallback(() => {
    const isCustomerMode = getCurrentSystemMode();
    const reviewedAt = isCustomerMode ? { customer: getSystemData() } : { representative: getSystemData() };

    const {
      by: termsReviewedBy,
      at: termsReviewedAt,
      information: termsReviewerSystemInformation,
    } = getLogInformation();

    updateGlobalData({
      ...data,
      auditLog: {
        ...auditLog,
        ...reviewedAt,
        termsSignedBy: isCustomerMode ? 'customer' : 'representative',
        customerSubmissionDate: dateTime.now().toISOString(),

        logVersion: 2,
        termsReviewerSystemInformation,
        termsReviewedAt,
        termsReviewedBy,
      },
      checkout: {
        ...checkout,
        termsReviewedByCustomer: true,
        syncDateStamp: dateTime.now().toISOString(),
      },
    });
  }, [auditLog, checkout, data, getLogInformation, getSystemData, updateGlobalData]);

  const setPaymentProcessed = useCallback(() => {
    const isCustomerMode = getCurrentSystemMode();
    if (isCustomerMode) return;

    const { at: checkoutProcessedAt, information: checkoutProcessorSystemInformation } = getLogInformation();

    updateGlobalData(
      {
        ...data,
        auditLog: {
          ...auditLog,
          checkoutProcessedAt,
          checkoutProcessorSystemInformation,
          representative: getSystemData(),
          checkoutDate: dateTime.now().toISOString(),
          logVersion: 2,
        },
        checkout: { ...checkout, paymentProcessed: true, paymentProcessingTimeStamp: dateTime.now().toISOString() },
      },
      true
    );
  }, [auditLog, checkout, data, getLogInformation, getSystemData, updateGlobalData]);

  const setAcceptTerms = useCallback(
    (termsAccepted: 'YES' | 'NO') => {
      const isLocked = termsAccepted === 'YES';

      const isCustomerMode = getCurrentSystemMode();
      const dto = {
        ...data,
        isLocked,
        checkout: {
          ...checkout,
          termsAccepted,
          termsAcceptedBy: isCustomerMode ? 'customer' : 'representative',
          termsAcceptanceTimeStamp: dateTime.now().toISOString(),
        },
      };

      if (isLocked) {
        const { backup } = data;
        const backupData = JSON.parse(backup || '[]');
        backupData.push(dto);
        dto.backup = JSON.stringify(backupData);
      }

      overrideGlobalData(dto, true);
      return dto;
    },
    [checkout, data, overrideGlobalData]
  );

  const resetCustomerConsent = useCallback(() => {
    const { backup } = data;

    const backupData = JSON.parse(backup || '[]');
    backupData.push(data);

    const dto = {
      ...data,
      isLocked: false,
      backup: JSON.stringify(backupData),
      checkout: {
        ...checkout,
        card_1: { ...checkoutStateModel.card_1 },
        card_2: { ...checkoutStateModel.card_2 },
        termsAccepted: '' as 'YES' | 'NO' | '',
        paymentProcessed: false,
        termsReviewedByCustomer: false,
        hasReviewedTerms: false,
        password: checkoutStateModel.password,
      },
      auditLog: {} as TGlobalDataModelAuditLog,
    };

    updateGlobalData(dto, true, true);
    return dto;
  }, [checkout, data, updateGlobalData]);

  return {
    setCustomerTermsReviewed,
    setPaymentProcessed,
    setAcceptTerms,
    resetCustomerConsent,
    getSystemData,
    getLogInformation,
  };
}
